/* Browser-native voice helpers — Web Speech API.
 *
 * STT: SpeechRecognition (webkitSpeechRecognition on Safari/Chrome).
 *      Streams interim + final transcripts. We don't ship a fallback;
 *      Firefox users see the mic button disabled with an explanatory
 *      title.
 *
 * TTS: SpeechSynthesis. Broad support (Chrome / Safari / Edge / Firefox).
 *      We cancel any in-flight utterance before starting a new one so
 *      streaming Claude responses don't queue up overlapping audio.
 */
import React from 'react';

const SR_CTOR =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition || null)
    : null;

/* Web Speech API support — present on Chrome/Edge desktop, Safari
 * (webkit prefix), and iOS Safari proper. NOT available in Firefox
 * desktop, and historically flaky in iOS PWA standalone mode.
 * When this is false, the chat falls back to MediaRecorder + the
 * server-side Whisper STT proxy. */
export const sttSupported = !!SR_CTOR;

/* MediaRecorder support — present in every modern browser including
 * iOS PWA standalone (where Web Speech API is missing). Lets us
 * record an audio clip + POST to /api/stt for Whisper transcription.
 * The combination is strictly more compatible than Web Speech API
 * alone, just at the cost of a server round-trip per utterance. */
/* ── Tones (mode-on / mode-off feedback) ──────────────────────────
 *
 * Replaces the spoken "I'm listening" greeting with sub-200 ms tones
 * so the user gets instant feedback when voice mode toggles.
 * playTone(payload) is synthesised in-browser via Web Audio API —
 * no network round-trip, no TTS latency.
 *
 *   playOnTone()   — rising note (G5 → C6, 160 ms total)
 *   playOffTone()  — falling note (C6 → G5, 140 ms)
 *
 * Both swallow errors silently and respect any active AudioContext.
 */
let toneCtx = null;
function getToneCtx() {
  if (toneCtx && toneCtx.state !== 'closed') return toneCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    toneCtx = new Ctx();
    return toneCtx;
  } catch { return null; }
}

function playNote(ctx, freq, startTime, duration, peakGain) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.015);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.02);
}

export function playOnTone() {
  try {
    const ctx = getToneCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
    const t = ctx.currentTime;
    // Rising two-note arpeggio — bright, optimistic.
    playNote(ctx, 784,  t,        0.08, 0.22);  // G5
    playNote(ctx, 1047, t + 0.08, 0.10, 0.22);  // C6
  } catch { /* tone is best-effort */ }
}

export function playOffTone() {
  try {
    const ctx = getToneCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
    const t = ctx.currentTime;
    // Falling note — softer, "closing down".
    playNote(ctx, 1047, t,        0.07, 0.18);  // C6
    playNote(ctx, 784,  t + 0.07, 0.10, 0.18);  // G5
  } catch {}
}

export const mediaRecorderSupported =
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === 'function';
export const ttsSupported =
  typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined';

/**
 * One-shot dictation hook. Call start() to begin listening; the
 * `transcript` updates live (interim + final). Call stop() to end —
 * onFinal fires with the final transcript so the caller can auto-send.
 */
export function useDictation({ onFinal, lang } = {}) {
  const [listening, setListening] = React.useState(false);
  const [transcript, setTranscript] = React.useState('');
  const [error, setError] = React.useState(null);
  const recRef = React.useRef(null);

  const stop = React.useCallback(() => {
    const rec = recRef.current;
    if (rec) {
      try { rec.stop(); } catch {}
    }
    setListening(false);
  }, []);

  const start = React.useCallback(() => {
    if (!SR_CTOR) { setError('Voice input not supported in this browser'); return; }
    setError(null);
    setTranscript('');
    const rec = new SR_CTOR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang || navigator.language || 'en-US';

    let finalText = '';
    rec.onresult = (ev) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      setTranscript((finalText + ' ' + interim).trim());
    };
    rec.onerror = (ev) => {
      setError(ev.error || 'recognition failed');
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      const out = finalText.trim();
      if (out && onFinal) onFinal(out);
    };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      setError(err.message || String(err));
      setListening(false);
    }
  }, [lang, onFinal]);

  // Stop on unmount so a dictation doesn't outlive its component.
  React.useEffect(() => () => stop(), [stop]);

  return { listening, transcript, error, start, stop };
}

/**
 * Speak a single string of text. Cancels any in-flight utterance.
 * Pass an empty / null string to just stop. Returns nothing — fire
 * and forget; the browser handles queueing.
 */
export function speak(text, opts = {}) {
  if (!ttsSupported) return;
  const synth = window.speechSynthesis;
  try { synth.cancel(); } catch {}
  if (!text) return;
  const u = new SpeechSynthesisUtterance(stripForSpeech(text));
  u.lang = opts.lang || navigator.language || 'en-US';
  u.rate = opts.rate || 1.0;
  u.pitch = opts.pitch || 1.0;
  // Optional voice pick — caller can pass `voice` (a SpeechSynthesisVoice).
  if (opts.voice) u.voice = opts.voice;
  synth.speak(u);
}

export function cancelSpeech() {
  if (ttsSupported) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
  // Also stop any cloud-TTS playback. The singleton audio element
  // is created lazily on the first speakCloud() call.
  const a = cloudAudioRef.current;
  if (a) {
    try { a.pause(); a.removeAttribute('src'); a.load(); } catch {}
  }
}

/* ── Cloud TTS (OpenAI via /api/tts) ──────────────────────────────
 *
 * Plays the requested text via the server-side TTS proxy. Returns a
 * Promise that resolves when playback ends and rejects when the
 * proxy is unavailable (503 — OPENAI_API_KEY not configured) or any
 * other error path, so callers can fall back to the OS speech
 * synthesizer.
 *
 * iOS Safari requires the first audio.play() to happen inside a
 * user gesture; pass `unlockedFromGesture: true` on the first call
 * to mark the singleton as primed. Once primed, subsequent calls
 * from any context play normally.
 *
 * Voice is the OpenAI voice name (default 'nova') — passed through
 * to the proxy.
 */
const cloudAudioRef = { current: null };
// Token incremented on every speakCloud invocation; in-flight calls
// compare against this to detect preemption (a newer call took over
// the singleton audio element). Prevents stale promises + stale
// event listeners from firing resume callbacks for utterances that
// were cancelled.
let cloudUtteranceToken = 0;
let cloudPrimed = false;

export function isCloudTtsPrimed() { return cloudPrimed; }

function ensureCloudAudio() {
  if (cloudAudioRef.current) return cloudAudioRef.current;
  const a = new Audio();
  a.preload = 'auto';
  cloudAudioRef.current = a;
  return a;
}

/**
 * Fetch a TTS audio blob without playing it. The result is a small
 * payload { blob, url } the caller can pass to playTtsPayload to
 * actually play. Splitting fetch from play lets drainTtsQueue
 * pre-fetch chunk N+1 while chunk N is still playing, eliminating
 * the inter-chunk silence that single-shot speakCloud produced.
 */
export async function prefetchTtsPayload(text, opts = {}) {
  const cleaned = stripForSpeech(String(text || '')).trim();
  if (!cleaned) return null;
  const res = await fetch('/api/tts', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text: cleaned, voice: opts.voice || 'shimmer' }),
  });
  if (!res.ok) {
    const err = new Error(res.status === 503 ? 'tts-disabled' : `tts-${res.status}`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  return { blob, url: URL.createObjectURL(blob) };
}

/**
 * Play a previously-prefetched TTS payload on the singleton audio
 * element. Returns a Promise that resolves on `ended` and rejects
 * on playback errors. The payload's blob URL is revoked on
 * cleanup so we don't leak memory across long conversations.
 */
export function playTtsPayload(payload) {
  if (!payload || !payload.url) return Promise.resolve();
  const audio = ensureCloudAudio();
  const myToken = ++cloudUtteranceToken;
  try { audio.pause(); } catch {}

  return new Promise((resolve, reject) => {
    const release = () => {
      // iOS needs an explicit session release after playback — empty
      // src + load() flips the AVAudioSession back to a state where
      // SpeechRecognition / MediaRecorder can re-acquire the mic.
      try { audio.pause(); } catch {}
      try { audio.removeAttribute('src'); audio.load(); } catch {}
      try { URL.revokeObjectURL(payload.url); } catch {}
    };
    const cleanup = () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      release();
    };
    const onEnded = () => {
      if (myToken !== cloudUtteranceToken) { cleanup(); reject(new Error('preempted')); return; }
      cleanup(); resolve();
    };
    const onError = () => { cleanup(); reject(new Error('audio playback failed')); };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.src = payload.url;
    audio.play()
      .then(() => { cloudPrimed = true; })
      .catch((err) => { cleanup(); reject(err); });
  });
}

/**
 * Convenience: fetch + play in one call. Kept for callers that
 * don't need the prefetch optimization (e.g. the entry greeting).
 */
export async function speakCloud(text, opts = {}) {
  const payload = await prefetchTtsPayload(text, opts);
  if (!payload) return;
  return playTtsPayload(payload);
}

/**
 * Markdown / code-block sanitisation for TTS — the synthesizer
 * reads everything literally, so backticks, asterisks, and link
 * URLs end up pronounced. Stripping them is enough; we don't try
 * to do anything clever with structure.
 */
function stripForSpeech(s) {
  return String(s)
    .replace(/```[\s\S]*?```/g, ' code block omitted ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\|/g, ' ')
    .replace(/-{3,}/g, '');
}

/* ── Persistent mic stream ────────────────────────────────────────
 *
 * Acquiring a MediaStream via getUserMedia is expensive on iOS —
 * ~300-500 ms for the OS to grant + initialize the audio session.
 * For a multi-turn conversation we keep ONE stream open across turns
 * and just spin up a fresh MediaRecorder per utterance. The visible
 * iOS "in-use" indicator stays on for the duration of conversation
 * mode (matching what the user already consented to by tapping the
 * mic button), and turn-taking is instant.
 *
 * Call acquireMicStream() before the first recording. Call
 * releaseMicStream() when leaving conversation mode so the OS
 * properly releases the mic + drops the red status indicator.
 */
let persistentMicStream = null;
let persistentAudioCtx = null;
let persistentAnalyser = null;

export async function acquireMicStream() {
  if (persistentMicStream) return persistentMicStream;
  if (!mediaRecorderSupported) return null;
  persistentMicStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  // Build the analyser once too — VAD reuses it across recordings.
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    persistentAudioCtx = new Ctx();
    // iOS Safari ships the AudioContext in 'suspended' state until
    // a user gesture explicitly resumes it. Without this resume the
    // AnalyserNode below always reads silence (samples == 128),
    // sawSpeechEver stays false, the VAD never closes the
    // recording, and the user's voice never reaches Whisper.
    // Caller MUST invoke acquireMicStream from inside the gesture
    // (we do — cycleVoiceMode is a click handler).
    if (persistentAudioCtx.state === 'suspended') {
      try { await persistentAudioCtx.resume(); } catch { /* iOS sometimes throws on already-resumed */ }
    }
    const source = persistentAudioCtx.createMediaStreamSource(persistentMicStream);
    persistentAnalyser = persistentAudioCtx.createAnalyser();
    persistentAnalyser.fftSize = 1024;
    source.connect(persistentAnalyser);
  } catch { /* analyser is optional — VAD will fall back to maxDuration */ }
  return persistentMicStream;
}

export function releaseMicStream() {
  try { persistentMicStream?.getTracks().forEach(t => t.stop()); } catch {}
  try { persistentAudioCtx?.close(); } catch {}
  persistentMicStream = null;
  persistentAudioCtx = null;
  persistentAnalyser = null;
}

/** Current input level (RMS) 0-128 — used by callers to drive a
 *  waveform visualization. Returns 0 when no stream is active. */
export function micRmsSample() {
  if (!persistentAnalyser) return 0;
  const buf = new Uint8Array(persistentAnalyser.fftSize);
  persistentAnalyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) { const d = buf[i] - 128; sum += d * d; }
  return Math.sqrt(sum / buf.length);
}

/* ── MediaRecorder + Whisper STT (fallback for browsers without
 *    Web Speech API support — primarily iOS PWA standalone, Firefox
 *    desktop) ────────────────────────────────────────────────────
 *
 * Records audio via MediaRecorder while VAD via AnalyserNode tracks
 * the input level. When the user goes silent for `silenceMs`, we
 * stop the recorder, ship the blob to /api/stt, and resolve with
 * the transcript. The caller wires that text into the chat composer.
 *
 * Usage:
 *   const handle = await startWhisperRecording({
 *     silenceMs: 1200,                      // stop after this much silence
 *     maxDurationMs: 30_000,                // hard cap so it never runs forever
 *     onPartial: (text) => { ... },         // optional: per-chunk interim text
 *     onFinal:   (text) => { ... },         // final transcript
 *   });
 *   // ... user speaks ...
 *   handle.stop();   // optional manual stop; auto-stops on silence
 *
 * Returns a handle { stop(), cancel() } so the caller can tear down
 * cleanly (e.g. when the user toggles voiceMode off mid-recording).
 */
export async function startWhisperRecording(opts = {}) {
  const silenceMs = opts.silenceMs || 1200;
  // Hard cap. Lowered to 8 s so even when VAD fails (suspended
  // AudioContext, browser-side audio routing bugs) the recording
  // ALWAYS terminates and either gets transcribed or politely
  // gives up. 30 s was too long — users gave up before the cap
  // fired. Conversation-style utterances are almost always < 8 s.
  const maxDurationMs = opts.maxDurationMs || 8_000;
  // If we never see any speech (sawSpeechEver stays false), bail
  // earlier than maxDuration so the next recording cycle can start
  // fresh — usually a permission/setup glitch is the cause.
  const noSpeechBailMs = opts.noSpeechBailMs || 5_000;
  const onFinal = opts.onFinal || (() => {});
  const onError = opts.onError || (() => {});
  // Optional conversation prompt — Whisper accepts ~250 tokens of
  // context that bias the transcription toward the conversation's
  // lexicon. Boosts accuracy on names, technical terms, and code
  // switches between languages.
  const prompt = typeof opts.prompt === 'string' ? opts.prompt.slice(-900) : '';

  let stream;
  try { stream = await acquireMicStream(); }
  catch (err) { onError(err); return { stop: () => {}, cancel: () => {} }; }
  if (!stream) { onError(new Error('MediaRecorder API not available')); return { stop: () => {}, cancel: () => {} }; }

  // Pick a MIME the browser can actually encode. Safari prefers
  // mp4/m4a; everyone else does webm/opus. Whisper accepts both.
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mpeg',
    'audio/ogg;codecs=opus',
  ];
  let mimeType = '';
  for (const m of candidates) {
    if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(m)) {
      mimeType = m;
      break;
    }
  }
  const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  const chunks = [];
  let stopped = false;
  let silenceTimer = null;
  let maxTimer = null;
  let vadInterval = null;

  const cleanup = () => {
    stopped = true;
    if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
    if (maxTimer) { clearTimeout(maxTimer); maxTimer = null; }
    if (vadInterval) { clearInterval(vadInterval); vadInterval = null; }
    // DO NOT stop the stream tracks here — they're shared across
    // turns via the persistent stream. releaseMicStream() at
    // conversation-off handles teardown.
  };

  // Known Whisper hallucination patterns. The model "fills in"
  // these phrases when fed silence or near-silence — they come from
  // captioning/subtitle training data and otherwise look like real
  // transcripts. We drop them BEFORE they reach the chat so the
  // assistant doesn't get bogus user turns.
  const WHISPER_HALLUCINATIONS = [
    /^untertitel.*amara/i,
    /^subtitles?\b.*amara/i,
    /^subtitles?\b.*by\b/i,
    /^untertitel(ung)?\b/i,
    /^thanks?\s+for\s+watching/i,
    /^thank\s+you\s+for\s+watching/i,
    /^(please\s+)?subscribe\b/i,
    /^ご視聴ありがとうございました/,
    /^字幕/,
    /^\s*\[?\s*(music|applause|laughter|silence)\s*\]?\s*$/i,
    /^♪+/,
    /^[\.…\-—\s]+$/,
  ];
  const isHallucination = (s) => {
    const t = String(s || '').trim();
    if (!t) return true;
    if (t.length < 3) return true;
    return WHISPER_HALLUCINATIONS.some(re => re.test(t));
  };

  const finalize = async () => {
    if (chunks.length === 0) { onFinal(''); return; }
    const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
    // Tiny blobs (< ~3 KB of compressed audio) usually = no real
    // speech captured. Skip the Whisper round-trip + the cost.
    if (blob.size < 3000) { onFinal(''); return; }
    // Map MIME → file extension so Whisper sniffs the format right.
    const ext = mimeType.includes('mp4')  ? 'm4a'
              : mimeType.includes('mpeg') ? 'mp3'
              : mimeType.includes('ogg')  ? 'ogg'
              :                              'webm';
    const form = new FormData();
    form.append('audio', blob, `audio.${ext}`);
    if (prompt) form.append('prompt', prompt);
    try {
      const res = await fetch('/api/stt', {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
      });
      if (!res.ok) { onError(new Error(`stt-${res.status}`)); return; }
      const json = await res.json();
      const text = String(json.text || '').trim();
      // Drop known hallucinations BEFORE handing the text upstream.
      if (isHallucination(text)) {
        onFinal('');
        return;
      }
      onFinal(text);
    } catch (err) {
      onError(err);
    }
  };

  rec.addEventListener('dataavailable', (ev) => {
    if (ev.data && ev.data.size > 0) chunks.push(ev.data);
  });
  rec.addEventListener('stop', () => { cleanup(); finalize(); });
  rec.addEventListener('error', (ev) => { onError(ev.error || new Error('recorder error')); cleanup(); });

  // VAD on the persistent analyser. Falls back to maxDuration only
  // if the analyser couldn't be built (rare — only happens when
  // AudioContext construction fails).
  if (persistentAnalyser) {
    const buf = new Uint8Array(persistentAnalyser.fftSize);
    // 0-128 range. Tuned conservatively LOW so quiet speech (women's
    // voices, accented English, low-volume mic) still triggers
    // sawSpeechEver. Previous value of 8 caused some users to never
    // reach the "started speaking" state, so VAD never closed the
    // recording and the noSpeechTimer cancelled the whole session.
    const SILENCE_RMS = 4;
    let silentSince = null;
    let sawSpeechEver = false;
    vadInterval = setInterval(() => {
      if (stopped) return;
      persistentAnalyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const d = buf[i] - 128; sum += d * d; }
      const rms = Math.sqrt(sum / buf.length);
      if (rms < SILENCE_RMS) {
        if (sawSpeechEver) {
          if (silentSince === null) silentSince = Date.now();
          if (Date.now() - silentSince >= silenceMs) {
            try { rec.stop(); } catch {}
          }
        }
      } else {
        sawSpeechEver = true;
        silentSince = null;
      }
    }, 100);
  }

  maxTimer = setTimeout(() => {
    try { rec.stop(); } catch {}
  }, maxDurationMs);

  // No-speech bail: if VAD never reported sawSpeechEver within
  // noSpeechBailMs, assume something's wrong with the audio
  // pipeline (suspended AudioContext, mic permission revoked,
  // …) and stop early so the next recording cycle can try fresh.
  // The recording will still go to Whisper — most likely Whisper
  // returns empty text, the caller restarts via the onFinal path.
  setTimeout(() => {
    // Inspect the VAD flag indirectly: if we're still running and
    // VAD hasn't tripped the silence stop yet, force it.
    if (!stopped && persistentAnalyser) {
      // Read the analyser once to check — if it's silent again we
      // bail; if there's signal we let VAD do its job.
      const probe = new Uint8Array(persistentAnalyser.fftSize);
      persistentAnalyser.getByteTimeDomainData(probe);
      let sum = 0;
      for (let i = 0; i < probe.length; i++) { const d = probe[i] - 128; sum += d * d; }
      const rms = Math.sqrt(sum / probe.length);
      if (rms < 4) {  // matches SILENCE_RMS in vadInterval
        try { rec.stop(); } catch {}
      }
    }
  }, noSpeechBailMs);

  try { rec.start(250); /* 250 ms chunks */ } catch (err) { onError(err); cleanup(); }

  return {
    stop: () => { try { rec.stop(); } catch {} },
    cancel: () => { stopped = true; try { rec.stop(); } catch {}; cleanup(); },
  };
}
